<template>
  <div class="payment-page">
    <AppHeader />
    <div class="payment-container">
      <div v-if="loading" class="payment-result loading">
        <div class="loading-spinner"></div>
         <h2>{{ $t('legacy.Проверка платежа...') }}</h2>
         <p>{{ $t('legacy.Пожалуйста, подождите') }}</p>
      </div>

      <div v-else-if="status === 'succeeded'" class="payment-result success">
        <div class="result-icon">
          <svg width="80" height="80" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" fill="#4CAF50"/>
            <path d="M9 12L11 14L15 10" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <template v-if="isCreditPurchase">
           <h2>{{ $t('legacy.Кредиты начислены!') }}</h2>
           <p>{{ $t('legacy.ИИ-кредиты успешно добавлены на ваш аккаунт') }}</p>
        </template>
        <template v-else>
           <h2>{{ $t('legacy.Оплата успешна!') }}</h2>
           <p>{{ $t('legacy.dynamic.subscriptionActivated', { plan: planName }) }}</p>
          <p v-if="paymentProvider" class="expiry-info">{{ $t('legacy.paymentProviderUsed', { provider: paymentProviderLabel }) }}</p>
          <p v-if="subscriptionEndDate" class="expiry-info">
             {{ $t('legacy.Действует до:') }} {{ formatDate(subscriptionEndDate) }}
          </p>
        </template>
        <router-link to="/personal-subscription" class="continue-btn">
           {{ $t('legacy.Перейти к подписке') }}
        </router-link>
      </div>

      <div v-else-if="status === 'pending'" class="payment-result pending">
        <div class="result-icon">
          <svg width="80" height="80" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" fill="#FF9800"/>
            <path d="M12 8V12L14 14" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
         <h2>{{ $t('legacy.Платёж обрабатывается') }}</h2>
         <p v-if="paymentProvider">{{ $t('legacy.paymentProviderUsed', { provider: paymentProviderLabel }) }}</p>
         <p v-if="isCreditPurchase">{{ $t('legacy.paymentCreditsPending') }}</p>
         <p v-else>{{ $t('legacy.paymentSubscriptionPending') }}</p>
        <button class="check-btn" @click="checkStatus" :disabled="checking">
           {{ checking ? $t('legacy.Проверка...') : $t('legacy.Проверить статус') }}
        </button>
        <router-link to="/subscription/plans" class="back-btn">
           {{ $t('legacy.Вернуться к тарифам') }}
        </router-link>
      </div>

      <div v-else-if="status === 'canceled'" class="payment-result canceled">
        <div class="result-icon">
          <svg width="80" height="80" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" fill="#9E9E9E"/>
            <path d="M15 9L9 15M9 9L15 15" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
         <h2>{{ $t('legacy.Платёж отменён') }}</h2>
         <p>{{ $t('legacy.Оплата была отменена. Вы можете попробовать снова.') }}</p>
        <router-link to="/subscription/plans" class="retry-btn">
           {{ $t('legacy.Выбрать тариф') }}
        </router-link>
      </div>

      <div v-else class="payment-result error">
        <div class="result-icon">
          <svg width="80" height="80" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" fill="#F44336"/>
            <path d="M15 9L9 15M9 9L15 15" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <h2>{{ errorTitle }}</h2>
        <p>{{ errorMessage }}</p>
        <router-link to="/subscription/plans" class="retry-btn">
           {{ $t('legacy.Попробовать снова') }}
        </router-link>
      </div>
    </div>
  </div>
</template>

<script>
import AppHeader from '@/components/AppHeader.vue';
import { getPaymentStatus } from '@/api/payments';
import { useToast } from 'vue-toastification';

export default {
  name: 'PaymentReturnPage',
  components: {
    AppHeader,
  },
  data() {
    return {
      loading: true,
      checking: false,
      status: null,
      planName: '',
      planRaw: '',
      amount: null,
      paymentId: null,
      paymentProvider: null,
      subscriptionEndDate: null,
      isCreditPurchase: false,
       errorTitle: '',
       errorMessage: '',
      toast: null,
    };
  },
  async created() {
    this.toast = useToast();
    
    const urlParams = new URLSearchParams(window.location.search);
    this.paymentId = urlParams.get('paymentId') || urlParams.get('localPaymentId')
      || localStorage.getItem('lastPaymentId');
    
    if (this.paymentId) {
      await this.checkStatus();
    } else {
      this.loading = false;
      this.status = 'error';
       this.errorTitle = this.$t('legacy.Платёж не найден');
       this.errorMessage = this.$t('legacy.paymentNotFound');
    }
  },
  computed: {
    paymentProviderLabel() {
      if (this.paymentProvider === 'prodamus') return 'Prodamus';
      if (this.paymentProvider === 'yookassa') return 'YooKassa';
      return this.paymentProvider;
    },
  },
  methods: {
    async checkStatus() {
      if (!this.paymentId) return;
      
      this.checking = true;
      
      try {
        const response = await getPaymentStatus(this.paymentId);
        this.status = response.status;
        this.paymentProvider = response.provider || null;
        this.planRaw = response.plan || '';
        this.isCreditPurchase = this.planRaw.startsWith('credits_');
        this.planName = this.getPlanDisplayName(response.plan);
        this.amount = response.amount;
        
        if (response.status === 'succeeded') {
          if (localStorage.getItem('lastPaymentId') === this.paymentId) {
            localStorage.removeItem('lastPaymentId');
          }
          await this.$store.dispatch('auth/loadUser');
          
          if (this.isCreditPurchase) {
             this.toast.success(this.$t('legacy.ИИ-кредиты успешно начислены!'));
          } else {
            const user = this.$store.state.auth.user;
            if (user && user.subscription_end_date) {
              this.subscriptionEndDate = user.subscription_end_date;
            }
             this.toast.success(this.$t('legacy.Подписка успешно активирована!'));
          }
        }
      } catch (err) {
        console.error('Error checking payment status:', err);
        this.status = 'error';
         this.errorTitle = this.$t('common.error');
         this.errorMessage = err.response?.data?.error || this.$t('legacy.Не удалось проверить статус платежа');
      } finally {
        this.loading = false;
        this.checking = false;
      }
    },
    getPlanDisplayName(plan) {
      const names = {
         base: this.$t('legacy.Базовый'),
         premium: this.$t('legacy.Премиум')
      };
      return names[plan] || plan || '';
    },
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
       return this.$formatDate(date, {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    }
  }
};
</script>

<style scoped>
.payment-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa, #e8ecef);
}

.payment-container {
  max-width: 500px;
  margin: 0 auto;
  padding: 40px 20px;
}

.payment-result {
  background: #fff;
  border-radius: 16px;
  padding: 48px 32px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  text-align: center;
}

.payment-result.loading {
  padding: 64px 32px;
}

.loading-spinner {
  width: 48px;
  height: 48px;
  border: 4px solid #e0e0e0;
  border-top-color: #496782;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 24px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.result-icon {
  margin-bottom: 24px;
}

.payment-result h2 {
  font-size: 24px;
  font-weight: 700;
  margin: 0 0 12px 0;
}

.payment-result.success h2 {
  color: #4CAF50;
}

.payment-result.pending h2 {
  color: #FF9800;
}

.payment-result.canceled h2 {
  color: #9E9E9E;
}

.payment-result.error h2 {
  color: #F44336;
}

.payment-result.loading h2 {
  color: #496782;
}

.payment-result p {
  font-size: 16px;
  color: #666;
  margin: 0 0 8px 0;
}

.expiry-info {
  font-weight: 500;
  color: #496782 !important;
  margin-bottom: 24px !important;
}

.continue-btn, .retry-btn {
  display: inline-block;
  padding: 14px 32px;
  background: linear-gradient(135deg, #496782, #5a7a99);
  color: #fff;
  font-size: 16px;
  font-weight: 600;
  border-radius: 8px;
  text-decoration: none;
  transition: all 0.2s;
  margin-top: 16px;
  border: none;
  cursor: pointer;
}

.continue-btn:hover, .retry-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(73, 103, 130, 0.3);
}

.check-btn {
  display: inline-block;
  padding: 14px 32px;
  background: linear-gradient(135deg, #496782, #5a7a99);
  border: none;
  color: #fff;
  font-size: 16px;
  font-weight: 600;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  margin-top: 16px;
}

.check-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(73, 103, 130, 0.3);
}

.check-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.back-btn {
  display: block;
  margin-top: 16px;
  color: #666;
  text-decoration: none;
  font-size: 14px;
}

.back-btn:hover {
  color: #496782;
}

@media (max-width: 768px) {
  .payment-container {
    padding: 20px 16px;
  }

  .payment-result {
    padding: 32px 20px;
  }
}
</style>
